import React from "react";
import { Chart } from "primereact/chart";

const options = {
    maintainAspectRatio: false,
    aspectRatio: 1.8,
    plugins: {
        legend: {
            labels: { usePointStyle: true },
        },
        tooltip: {
            callbacks: {
                label: function (context) {
                    let label = context.dataset.label || '';
                    let value = context.parsed.y !== undefined ? context.parsed.y : context.parsed;
                    // Format as euro for all series
                    return `${label}: ${value.toLocaleString()}€`;
                }
            }
        }
    },
    scales: {
        x: {
            stacked: true, // enable stacking
            grid: {
                display: true,
                drawOnChartArea: true,
            },
        },
        y: {
            stacked: true, // enable stacking
            ticks: { precision: 0 },
            grid: {
                borderDash: [6, 4],
                tickBorderDash: [6, 4],
                drawOnChartArea: true,
            },
        },
    },
};


export default function CentralPanel({ reportData, dateRange }) {
    const data = reportData;

    // Guard against null data
    if (!data) {
        return (
            <div style={{ padding: 24, textAlign: 'center', color: '#999', background: '#f9fafb', borderRadius: 8 }}>
                <p>Zoho Books integration not available. Please ensure your Zoho Books integration is properly connected.</p>
            </div>
        );
    }

    const { reportTitle, reportDate, summary, metrics } = data;
    let { performanceOverview, categories } = data;

    // Filter chart data based on dateRange if provided
    if (dateRange && Array.isArray(dateRange) && dateRange.length === 2) {
        const [startDate, endDate] = dateRange;
        const labels = performanceOverview.labels || categories || [];

        // Find indices of dates within range
        const startIdx = labels.findIndex(label => {
            const labelDate = new Date(label);
            return labelDate >= new Date(startDate);
        });
        const endIdx = labels.findIndex(label => {
            const labelDate = new Date(label);
            return labelDate > new Date(endDate);
        });

        if (startIdx !== -1) {
            const finalEndIdx = endIdx === -1 ? labels.length : endIdx;

            // Filter labels and data
            performanceOverview = {
                ...performanceOverview,
                labels: labels.slice(startIdx, finalEndIdx),
                datasets: performanceOverview.datasets.map(ds => ({
                    ...ds,
                    data: ds.data?.slice(startIdx, finalEndIdx) || ds.data,
                })),
            };
        }
    }

    // Add chart formatting properties to datasets
    performanceOverview = {
        ...performanceOverview,
        datasets: performanceOverview.datasets.map((ds) => {
            if (ds.label === "Costs") {
                return {
                    ...ds,
                    backgroundColor: "#FA4E57",
                    borderRadius: 6,
                    order: 3,
                    stack: 'stack1', // assign to stack1
                };
            }
            if (ds.label === "Gastos de explotación") {
                return {
                    ...ds,
                    backgroundColor: "#F9B572",
                    borderRadius: 6,
                    order: 4,
                    stack: 'stack1', // assign to stack1
                };
            }
            if (ds.label === "Income") {
                return {
                    ...ds,
                    backgroundColor: "#4BC293",
                    borderRadius: 6,
                    order: 2,
                };
            }
            if (ds.label === "Profit") {
                return {
                    ...ds,
                    type: "line",
                    borderColor: "#1919F3",
                    fill: true,
                    order: 1,
                };
            }
            return ds;
        }),
    };

    // Fallback for labels if not present
    const chartLabels = performanceOverview.labels || categories || [];

    return (
        <div style={{ background: "#fff", borderRadius: 8, padding: 24, boxShadow: "0 1px 4px rgb(0 0 0 / 0.1)" }}>
            <h3 style={{ marginBottom: 4 }}>{reportTitle}</h3>
            <small style={{ color: "#888888", display: "block", marginBottom: 16 }}>{reportDate}</small>
            <p style={{ fontWeight: 600, marginBottom: 24 }}>{summary}</p>

            <div style={{ display: "flex", gap: 16, marginBottom: 32 }}>
                {Object.entries(metrics).map(([key, { value, changePct, isPositive }]) => (
                    <div
                        key={key}
                        style={{
                            flex: 1,
                            background: "#f7f8fa",
                            borderRadius: 8,
                            padding: "16px 24px",
                            minWidth: 0,
                        }}
                    >
                        <div style={{ fontSize: 12, color: "#999", marginBottom: 4, textTransform: "capitalize" }}>
                            {(() => {
                                const previouslastLabel = chartLabels.length > 1 ? chartLabels[chartLabels.length - 2] : '';
                                if (key === "revenue") return `Revenue (${previouslastLabel})`;
                                if (key === "grossMargin") return `Gross Margin (${previouslastLabel})`;
                                return `Contribution Margin (${previouslastLabel})`;
                            })()}
                        </div>
                        <div style={{ fontWeight: 700, fontSize: 20 }}>
                            {key === "revenue" ? `${value.toLocaleString()}€` : `${value.toFixed(1)}%`}
                        </div>
                        <div
                            style={{
                                color: isPositive ? "green" : "red",
                                fontSize: 12,
                                display: "flex",
                                alignItems: "center",
                                gap: 4,
                                marginTop: 4,
                            }}
                        >
                            {isPositive ? "↗" : "↘"} {Math.abs(changePct)}%
                        </div>
                    </div>
                ))}
            </div>

            <div
                style={{
                    background: "#f7f8fa",
                    borderRadius: 8,
                    padding: 24,
                    height: 420,
                    marginTop: 0,
                }}
            >
                <Chart
                    type="bar"
                    data={performanceOverview}
                    options={options}
                    style={{ height: "100%" }}
                />
            </div>
        </div>
    );
}